// src/utils/reasoningParser.js

/**
 * Detect if content is an AI refusal/meta-response instead of actual article content
 *
 * @param {string} content - Content to check
 * @returns {boolean} True if content appears to be an AI refusal
 */
export function isAIRefusal(content) {
  if (!content || typeof content !== 'string') {
    return false;
  }

  const lowerContent = content.toLowerCase();

  // Patterns that indicate AI refusal or meta-commentary
  const refusalPatterns = [
    // Direct refusals
    'i cannot ethically proceed',
    'i cannot ethically',
    'cannot ethically proceed',
    'i\'m unable to',
    'i am unable to',
    'i can\'t proceed',
    'i cannot proceed',
    'however, i cannot',
    'but i cannot',
    // Perplexity-specific refusals
    'i appreciate the creative brief',
    'i appreciate the creative prompt',
    'i need to clarify my role',
    'i\'m perplexity',
    'i am perplexity',
    'as perplexity',
    'my core instructions',
    'my core requirement',
    'conflicts with my core',
    'this conflicts with',
    'violate my citation requirements',
    'bypass my core requirement',
    // Meta-commentary
    'i need to be transparent',
    'would you like me to try again',
    'what would solve this',
    'which would be more useful',
    'what would be most useful',
    'instead, i can provide',
    'instead, i can',
    'i can either:',
    // Source/quality discussions
    'i cannot cite',
    'cannot cite youtube',
    'the search results provided',
    'the search results are incomplete',
    'if you can provide search results',
    'violates the professional journalism standards',
    'under modified sourcing rules',
    'blacklist youtube',
    'your character "',
    'your requirements',
    'credible source requirements',
    // Persona rejection
    'adopt a persona',
    'adopt this persona',
    'roleplay as',
    'take on the persona'
  ];

  // Check if content matches refusal patterns - single match is enough for strong patterns
  const strongRefusalPatterns = [
    'i appreciate the creative brief',
    'i need to clarify my role',
    'conflicts with my core',
    'the search results are incomplete',
    'which would be more useful',
    'what would be most useful',
    'i can either:',
    'i cannot write the',
    'the available search results contain',
    'i would need search results',
    'to deliver the policy-focused analysis'
  ];

  // Single strong pattern = refusal
  for (const pattern of strongRefusalPatterns) {
    if (lowerContent.includes(pattern)) {
      return true;
    }
  }

  // Check if content matches weaker refusal patterns
  const matchCount = refusalPatterns.filter(pattern =>
    lowerContent.includes(pattern)
  ).length;

  // If 2+ patterns match, it's likely a refusal
  if (matchCount >= 2) {
    return true;
  }

  // Also check for specific structural patterns of refusals
  // Refusals often have "What I can tell you:" followed by "However, I cannot"
  if (lowerContent.includes('what i can tell you') &&
      (lowerContent.includes('however') || lowerContent.includes('but i cannot'))) {
    return true;
  }

  return false;
}

/**
 * Extract clean content from reasoning model output
 * Removes the reasoning section and returns only the formatted content
 *
 * @param {string} rawContent - Raw content from reasoning model
 * @returns {string} Clean content without reasoning section
 */
export function extractCleanContent(rawContent) {
    if (!rawContent || typeof rawContent !== 'string') {
      return '';
    }

    // FIRST: Strip <think>...</think> blocks entirely (case-insensitive, handles multiline)
    let content = rawContent.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();

    // Also strip orphaned opening <think> tags (when model doesn't close them)
    content = content.replace(/^<think>\s*/gi, '').trim();

    // If nothing left after stripping think tags, return empty
    if (!content) {
      return '';
    }

    // Check if this is an AI refusal - return empty if so
    if (isAIRefusal(content)) {
      console.warn('AI refusal detected in content, returning empty');
      return '';
    }

    // Look for the first markdown heading (# or ##) which typically starts the clean content
    // This regex matches headings at the start of a line, including bold headings like ## **Title**
    const headingRegex = /^(#{1,6})\s+(\*\*)?([^*\n]+)(\*\*)?/m;
    const headingMatch = content.match(headingRegex);
    
    if (headingMatch) {
      const startIndex = content.indexOf(headingMatch[0]);
      const cleanContent = content.substring(startIndex).trim();

      // Additional cleanup: remove any trailing reasoning artifacts
      return cleanContent
        .replace(/\n\n+$/, '') // Remove excessive trailing newlines
        .trim();
    }

    // Fallback 1: Look for common content patterns if no heading found
    const contentPatterns = [
      /^[A-Z][A-Z\s&]+:/, // All caps sections like "EXECUTIVE SUMMARY:"
      /^\*\*[A-Z][^*]+\*\*/, // Bold headers like **Market Snapshot**
      /^### [A-Z]/, // Level 3 headings
      /^• \*\*/, // Bullet with bold (Quick Hits format)
    ];

    for (const pattern of contentPatterns) {
      const match = content.match(pattern);
      if (match) {
        const startIndex = content.indexOf(match[0]);
        return content.substring(startIndex).trim();
      }
    }

    // Fallback 2: If content looks like it has reasoning, try to extract the end portion
    const reasoningIndicators = [
      'let me tackle',
      'looking at the search results',
      'the user wants',
      'i need to',
      'given the constraints',
      'after careful thought',
      'we must',
      'therefore, we'
    ];

    const hasReasoning = reasoningIndicators.some(indicator =>
      content.toLowerCase().includes(indicator)
    );

    if (hasReasoning) {
      // Try to find where reasoning ends and content begins
      // Look for a significant paragraph break followed by structured content
      const paragraphs = content.split('\n\n');

      // Find the first paragraph that looks like structured content
      for (let i = 0; i < paragraphs.length; i++) {
        const paragraph = paragraphs[i].trim();

        // Skip empty paragraphs
        if (!paragraph) continue;

        // Check if this paragraph looks like structured content
        if (
          paragraph.match(/^#{1,6}\s/) || // Markdown heading
          paragraph.match(/^\*\*[^*]+\*\*/) || // Bold header
          paragraph.match(/^[A-Z][A-Z\s&]+:/) || // All caps section
          paragraph.match(/^•\s/) || // Bullet point
          paragraph.match(/^-\s/) || // Dash bullet
          paragraph.match(/^\d+\./) // Numbered list
        ) {
          // Found structured content, return from this point
          const remainingParagraphs = paragraphs.slice(i);
          return remainingParagraphs.join('\n\n').trim();
        }
      }
    }

    // Final fallback: return content as-is
    console.warn('Could not parse reasoning content, returning as-is');
    return content.trim();
  }
  
  /**
   * Process content from reasoning model and apply citation processing
   * 
   * @param {string} rawContent - Raw content from reasoning model
   * @param {Array} sources - Array of source objects  
   * @param {string} sectionName - Name of the section being processed
   * @returns {string} Processed content ready for display
   */
  export function processReasoningContent(rawContent, sources, sectionName) {
    // First extract clean content
    const cleanContent = extractCleanContent(rawContent);
    
    // Then apply existing citation processing if available
    if (window.processCitations && typeof window.processCitations === 'function') {
      return window.processCitations(cleanContent, sources, sectionName);
    }
    
    // Fallback: return clean content without citation processing
    return cleanContent;
  }
  
  /**
   * Validate if content has been properly parsed
   *
   * @param {string} content - Processed content
   * @returns {Object} Validation result with status and issues
   */
  export function validateParsedContent(content) {
    const issues = [];

    // Check for AI refusals first
    if (isAIRefusal(content)) {
      issues.push('Content is an AI refusal/meta-response');
      return {
        isValid: false,
        isRefusal: true,
        issues: issues,
        contentLength: content.length
      };
    }

    // Check for reasoning artifacts that shouldn't be in final content
    const reasoningArtifacts = [
      'let me tackle',
      'looking at the search results',
      'the user wants',
      'given the constraints',
      'after careful thought',
      'we must follow the rules',
      'therefore, we'
    ];

    const foundArtifacts = reasoningArtifacts.filter(artifact =>
      content.toLowerCase().includes(artifact)
    );

    if (foundArtifacts.length > 0) {
      issues.push(`Reasoning artifacts found: ${foundArtifacts.join(', ')}`);
    }

    // Check for proper structure
    const hasStructure =
      content.includes('#') || // Has headings
      content.includes('**') || // Has bold text
      content.includes('•') || // Has bullets
      content.includes('-'); // Has dashes

    if (!hasStructure) {
      issues.push('Content lacks expected structure (headings, bold text, bullets)');
    }

    return {
      isValid: issues.length === 0,
      isRefusal: false,
      issues: issues,
      contentLength: content.length
    };
  }
  
  /**
   * Debug function to analyze reasoning model output
   * 
   * @param {string} rawContent - Raw content from reasoning model
   * @returns {Object} Analysis of the content structure
   */
  export function debugReasoningContent(rawContent) {
    const analysis = {
      totalLength: rawContent.length,
      hasHeadings: /^#{1,6}\s/m.test(rawContent),
      headingCount: (rawContent.match(/^#{1,6}\s/gm) || []).length,
      firstHeadingIndex: rawContent.search(/^#{1,6}\s/m),
      reasoningIndicators: [],
      paragraphCount: rawContent.split('\n\n').length,
      extractedContent: null,
      extractedLength: 0
    };
    
    // Check for reasoning indicators
    const indicators = [
      'let me tackle', 'looking at the search results', 'the user wants',
      'i need to', 'given the constraints', 'after careful thought'
    ];
    
    indicators.forEach(indicator => {
      if (rawContent.toLowerCase().includes(indicator)) {
        analysis.reasoningIndicators.push(indicator);
      }
    });
    
    // Try extraction
    const extracted = extractCleanContent(rawContent);
    analysis.extractedContent = extracted;
    analysis.extractedLength = extracted.length;
    analysis.extractionRatio = extracted.length / rawContent.length;
    
    return analysis;
  }